import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import SearchField from "../../DesignSystem/Inputs/SearchField";
import { PostCard } from "../../Adviser/Tabs/HelperFunctions/HelperFunctions";
import { ReactComponent as DropdownIcon } from "../../../assets/icons/paraplanningDashboard/ArrowDropdown.svg";
import { ReactComponent as SortIcon } from "../../../assets/icons/paraplanningDashboard/Sort.svg";
import { useUserEmail } from "../../SignIn/UserEmailContext";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { parsePlanTypeMappingFile } from "./utils/planTypeMappingParser";
import {
  getPlanTypeAuditLogs,
  getPlanTypeMappings,
  savePlanTypeMappings,
  updatePlanTypeIoFields,
} from "./utils/planTypeMappingService";
import { downloadPlanTypeMappingsExcel } from "./utils/exportPlanTypeMappingsExcel";
import { validatePlanTypeMappings } from "./utils/validatePlanTypeMappings";
import "../../../css/Adviser/LicenseCertificatesSummary.css";
import "../../../css/Adviser/AdviserDetailsPage.css";

const PlanTypeMappingPage = () => {
  const navigate = useNavigate();
  const { userEmail } = useUserEmail();
  const { userPermissions = [] } = useUserPermissions();
  const canManage = ["Administrator", "IO Admin"].some((permission) =>
    userPermissions.includes(permission),
  );
  const isAdmin = userPermissions.includes("Administrator");

  const [mappings, setMappings] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fixingIoFields, setFixingIoFields] = useState(false);
  const [ioMatchResult, setIoMatchResult] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "planTypeName",
    direction: "asc",
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [mappingRows, logs] = await Promise.all([
        getPlanTypeMappings(),
        isAdmin ? getPlanTypeAuditLogs() : Promise.resolve([]),
      ]);
      setMappings(mappingRows);
      setAuditLogs(logs);
    } catch (loadError) {
      setError(loadError.message || "Could not load IO plan type mappings.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (canManage) loadData();
  }, [canManage, loadData]);

  const invalidCount = useMemo(
    () => previewRows.filter((row) => !row.isValid).length,
    [previewRows],
  );

  const filteredMappings = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const matchingMappings = term
      ? mappings.filter((mapping) =>
          [
            mapping.planTypeId,
            mapping.planTypeName,
            mapping.planCategory,
            mapping.portfolioCategory,
            mapping.productGroup,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(term),
        )
      : mappings;

    return [...matchingMappings].sort((first, second) => {
      const comparison = String(first[sortConfig.key] || "").localeCompare(
        String(second[sortConfig.key] || ""),
        "en",
        { sensitivity: "base" },
      );

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [mappings, searchTerm, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleExcelDownload = async () => {
    setExporting(true);
    setError("");
    try {
      await downloadPlanTypeMappingsExcel(filteredMappings);
    } catch (exportError) {
      setError(exportError.message || "Could not create the Excel file.");
    } finally {
      setExporting(false);
    }
  };

  const handleCheckIoMatch = () => {
    setError("");
    const result = validatePlanTypeMappings(mappings);
    setIoMatchResult(result);

    if (result.isMatch) {
      window.alert(
        `IO Portfolio Category and IO Product Group match for all ${result.referenceCount} plan types.`,
      );
      return;
    }

    const details = [
      result.missing.length && `${result.missing.length} missing`,
      result.unexpected.length && `${result.unexpected.length} not found in IO`,
      result.mismatched.length && `${result.mismatched.length} with different details`,
    ].filter(Boolean);
    setError(`IO match check found: ${details.join(", ")}.`);
  };

  const handleFixIoFields = async () => {
    if (!ioMatchResult?.mismatched.length) return;
    setFixingIoFields(true);
    setError("");
    try {
      const result = await updatePlanTypeIoFields({
        differences: ioMatchResult.mismatched,
        actor: userEmail,
      });
      const refreshedMappings = mappings.map((mapping) => {
        const difference = ioMatchResult.mismatched.find(
          (item) => item.planTypeId === String(mapping.planTypeId),
        );
        return difference
          ? {
              ...mapping,
              portfolioCategory: difference.expected.portfolioCategory,
              productGroup: difference.expected.productGroup,
            }
          : mapping;
      });
      setMappings(refreshedMappings);
      const refreshedResult = validatePlanTypeMappings(refreshedMappings);
      setIoMatchResult(refreshedResult);
      setMessage(`${result.updatedCount} IO plan type mapping${result.updatedCount === 1 ? "" : "s"} fixed.`);
      if (refreshedResult.isMatch) {
        window.alert(
          `IO Portfolio Category and IO Product Group now match for all ${refreshedResult.referenceCount} plan types.`,
        );
      }
    } catch (fixError) {
      setError(fixError.message || "Could not fix the IO fields.");
    } finally {
      setFixingIoFields(false);
    }
  };

  if (!canManage) return <Navigate to="/Department/Compliance" replace />;

  const handlePreview = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const rows = await parsePlanTypeMappingFile(selectedFile);
      setPreviewRows(rows);
      const invalid = rows.filter((row) => !row.isValid).length;
      setMessage(
        `Review ready: ${rows.length - invalid} valid row${rows.length - invalid === 1 ? "" : "s"} and ${invalid} row${invalid === 1 ? "" : "s"} needing attention.`,
      );
    } catch (previewError) {
      setPreviewRows([]);
      setError(previewError.message || "Could not read the selected file.");
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!previewRows.length || invalidCount) return;
    setBusy(true);
    setError("");
    try {
      const result = await savePlanTypeMappings({
        rows: previewRows,
        actor: userEmail,
        sourceFileName: selectedFile?.name,
      });
      setMessage(`${result.savedCount} IO plan type mappings saved.`);
      setPreviewRows([]);
      setSelectedFile(null);
      setShowUpload(false);
      await loadData();
    } catch (saveError) {
      setError(
        saveError.message || "Could not save the IO plan type mappings.",
      );
    } finally {
      setBusy(false);
    }
  };

  const openEditPage = (mapping) => {
    navigate(
      `/compliance/io-plan-type-mapping/${encodeURIComponent(
        mapping.planTypeId,
      )}/edit`,
    );
  };

  return (
    <div className="main-content lcSummaryShell">
      <Helmet>
        <title>IO Plan Type Mapping</title>
      </Helmet>
      <Sidebar />
      <main
        className="Main-Container"
        style={{ overflowY: logsOpen ? "auto" : "hidden" }}
      >
        <div
          className="lcSummaryPage"
          style={{
            height: logsOpen ? "auto" : "100%",
            minHeight: logsOpen ? "100%" : 0,
          }}
        >
          <Breadcrumb
            items={[
              { label: "Compliance", to: "/Department/Compliance" },
              { label: "IO Plan Type Mapping" },
            ]}
          />

          <div className="lcSummaryHeader">
            <div className="lcSummaryTitleWrap">
              <div>
                <h3 className="FP_H3">IO Plan Type Mapping</h3>
                <p className="Content-Small muted">
                  Review and maintain Rosemount plan categories.
                </p>
              </div>
            </div>
            <div className="lcSummaryActions">
              <SearchField
                label=""
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search plan types"
                disabled={loading}
              />
              {/* <SecondaryButton
                label="Check IO fields"
                onClick={handleCheckIoMatch}
                disabled={loading || mappings.length === 0}
              /> */}
              <PrimaryButton
                label={exporting ? "Creating Excel..." : "Download Excel"}
                onClick={handleExcelDownload}
                disabled={loading || exporting || filteredMappings.length === 0}
              />
              {/* <PrimaryButton
                label={showUpload ? "Close upload" : "Upload mapping file"}
                onClick={() => {
                  setShowUpload((current) => !current);
                  setError("");
                  setMessage("");
                }}
              /> */}
            </div>
          </div>

          {!showUpload && error && (
            <div className="adviserLicenseError Content-Small">{error}</div>
          )}
          {!showUpload && message && (
            <p className="Content-Small muted">{message}</p>
          )}

          {ioMatchResult && !ioMatchResult.isMatch && (
            <section className="lcEditSection">
              <div className="lcSummaryHeader">
                <div>
                  <h3 className="FP_H3">IO field differences</h3>
                  <p className="Content-Small muted">
                    Rosemount Category is not included in this check.
                  </p>
                </div>
                {ioMatchResult.mismatched.length > 0 && (
                  <PrimaryButton
                    label={fixingIoFields ? "Fixing..." : `Fix ${ioMatchResult.mismatched.length} records`}
                    onClick={handleFixIoFields}
                    disabled={fixingIoFields}
                  />
                )}
              </div>
              <IoDifferencesTable result={ioMatchResult} />
            </section>
          )}

          {showUpload && (
            <section className="lcEditSection">
              <h3 className="FP_H3">Upload mapping file</h3>
              <p className="Content-Small muted">
                Choose a CSV or Excel file, review every row, then save it to
                Firebase.
              </p>
              <div className="lcSummaryActions">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(event) => {
                    setSelectedFile(event.target.files?.[0] || null);
                    setPreviewRows([]);
                    setError("");
                    setMessage("");
                  }}
                />
                <SecondaryButton
                  label={busy ? "Working..." : "Review file"}
                  disabled={!selectedFile || busy}
                  onClick={handlePreview}
                />
              </div>
              {error && (
                <div className="adviserLicenseError Content-Small">{error}</div>
              )}
              {message && <p className="Content-Small muted">{message}</p>}
            </section>
          )}

          {showUpload && previewRows.length > 0 && (
            <section className="lcEditSection">
              <div className="lcSummaryHeader">
                <h3 className="FP_H3">Review before saving</h3>
                <PrimaryButton
                  label={`Save ${previewRows.length} rows`}
                  disabled={busy || invalidCount > 0}
                  onClick={handleSave}
                />
              </div>
              {invalidCount > 0 && (
                <p className="adviserLicenseError Content-Small">
                  Correct {invalidCount} invalid row
                  {invalidCount === 1 ? "" : "s"} in the source file and review
                  it again before saving.
                </p>
              )}
              <MappingTable rows={previewRows} preview />
            </section>
          )}

          {loading ? (
            <p className="Content-Small muted">Loading mappings...</p>
          ) : (
            <MappingTable
              rows={filteredMappings}
              onEdit={openEditPage}
              preserveHeight={logsOpen}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          )}

          {isAdmin && (
            <PostCard
              title="Edit Logs"
              right={
                <button
                  type="button"
                  className="table-row-action"
                  onClick={() => setLogsOpen((current) => !current)}
                  aria-label={
                    logsOpen ? "Collapse edit logs" : "Expand edit logs"
                  }
                  aria-expanded={logsOpen}
                  title={logsOpen ? "Collapse edit logs" : "Expand edit logs"}
                >
                  <DropdownIcon
                    aria-hidden
                    style={{
                      transform: logsOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </button>
              }
            >
              {logsOpen ? (
                auditLogs.length === 0 ? (
                  <p className="Content-Small muted">
                    No edit logs found yet. New changes will appear here after
                    saving.
                  </p>
                ) : (
                  <div className="lcEditLogsList">
                    {auditLogs.map((log) => (
                      <div key={log.id} className="lcEditLogItem">
                        <div className="lcEditLogHeader">
                          <span className="FP_Help_Text_Semibold">
                            {log.performedBy || "Unknown user"}
                          </span>
                          <span className="FP_Help_Text muted">
                            {new Date(log.createdAt).toLocaleString("en-GB")}
                          </span>
                        </div>
                        <div className="lcEditLogChanges">
                          <div className="lcEditLogChange">
                            <div className="FP_Help_Text_Semibold">
                              {log.summary}
                            </div>
                            {log.action === "category_updated" ? (
                              <>
                                <div className="FP_Help_Text">
                                  <span className="muted">From:</span>{" "}
                                  {log.oldCategory || "-"}
                                </div>
                                <div className="FP_Help_Text">
                                  <span className="muted">To:</span>{" "}
                                  {log.newCategory || "-"}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="FP_Help_Text">
                                  <span className="muted">File:</span>{" "}
                                  {log.sourceFileName || "-"}
                                </div>
                                <div className="FP_Help_Text">
                                  <span className="muted">Rows:</span>{" "}
                                  {log.recordCount || 0}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : null}
            </PostCard>
          )}
        </div>
      </main>
    </div>
  );
};

const MappingTable = ({
  rows,
  preview = false,
  onEdit,
  preserveHeight = false,
  sortConfig,
  onSort,
}) => (
  <div
    className="lcTableWrap"
    style={
      preserveHeight
        ? { flex: "0 0 auto", height: "60vh", minHeight: "420px" }
        : undefined
    }
  >
    <table className="lcTable">
      <thead>
        <tr>
          <th
            className="lcIndexColumn lcPinnedColumn"
            aria-label="Row number"
          />
          <th
            className={`lcPinnedColumn lcPinnedColumnLeft ${
              onSort ? "lcSortableHead" : ""
            }`}
            onClick={onSort ? () => onSort("planTypeName") : undefined}
            aria-sort={
              !onSort
                ? undefined
                : sortConfig?.key === "planTypeName"
                ? sortConfig.direction === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
            title={onSort ? "Sort by IO plan type name" : undefined}
          >
            <div className="ParaplanningTableHeaderButton">
              <span className="FP_Help_Text_Semibold">IO Plan Type Name</span>
              {onSort && (
                <span
                  className="lcSortIcon"
                  style={{
                    opacity: sortConfig?.key === "planTypeName" ? 1 : 0.65,
                  }}
                  aria-hidden
                >
                  <SortIcon width={14} height={14} />
                </span>
              )}
            </div>
          </th>
          <th
            className={onSort ? "lcSortableHead" : undefined}
            onClick={onSort ? () => onSort("planCategory") : undefined}
            aria-sort={
              !onSort
                ? undefined
                : sortConfig?.key === "planCategory"
                ? sortConfig.direction === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
            title={onSort ? "Sort by Rosemount category" : undefined}
          >
            <div className="ParaplanningTableHeaderButton">
              <span className="FP_Help_Text_Semibold">
                Rosemount Category
              </span>
              {onSort && (
                <span
                  className="lcSortIcon"
                  style={{
                    opacity: sortConfig?.key === "planCategory" ? 1 : 0.65,
                  }}
                  aria-hidden
                >
                  <SortIcon width={14} height={14} />
                </span>
              )}
            </div>
          </th>
          {[
            "IO Plan Type ID",
            "IO Portfolio Category",
            "IO Product Group",
          ].map((heading) => (
            <th key={heading}>
              <div className="ParaplanningTableHeaderButton">
                <span className="FP_Help_Text_Semibold">{heading}</span>
              </div>
            </th>
          ))}
          {preview && (
            <th>
              <div className="ParaplanningTableHeaderButton">
                <span className="FP_Help_Text_Semibold">Validation</span>
              </div>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td className="lcEmptyCell" colSpan={preview ? 7 : 6}>
              No IO plan type mappings found.
            </td>
          </tr>
        ) : (
          rows.map((row, index) => (
            <tr key={`${row.planTypeId}-${row.rowNumber || ""}`}>
              <td className="lcIndexColumn lcPinnedColumn">
                <span className="FP_Help_Text primary-text">
                  {preview ? row.rowNumber : `${index + 1}.`}
                </span>
              </td>
              <td className="lcPinnedColumn lcPinnedColumnLeft lcWrappedCell">
                <span
                  className={`FP_Help_Text primary-text lcWrappedCell ${
                    preview ? "" : "lcClickableTextCell"
                  }`}
                  role={preview ? undefined : "button"}
                  tabIndex={preview ? undefined : 0}
                  onClick={() => !preview && onEdit?.(row)}
                  onKeyDown={(event) => {
                    if (
                      !preview &&
                      (event.key === "Enter" || event.key === " ")
                    ) {
                      event.preventDefault();
                      onEdit?.(row);
                    }
                  }}
                >
                  {row.planTypeName}
                </span>
              </td>
              {[
                row.planCategory,
                row.planTypeId,
                row.portfolioCategory,
                row.productGroup,
              ].map((value, valueIndex) => (
                <td key={`${row.planTypeId}-${valueIndex}`}>
                    <span
                      className={`${
                        valueIndex === 0
                          ? "FP_Help_Text_Semibold"
                          : "FP_Help_Text"
                      } black-text`}
                    >
                    {value || "-"}
                  </span>
                </td>
              ))}
              {preview && (
                <td>
                  <span className="FP_Help_Text black-text">
                    {row.errors.length ? row.errors.join("; ") : "Ready"}
                  </span>
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const IO_FIELD_LABELS = {
  portfolioCategory: "IO Portfolio Category",
  productGroup: "IO Product Group",
};

const IoDifferencesTable = ({ result }) => {
  const changedRows = result.mismatched.flatMap((item) =>
    item.fields.map((difference) => ({
      ...difference,
      planTypeId: item.planTypeId,
      planTypeName: item.planTypeName,
    })),
  );

  return (
    <div className="lcTableWrap" style={{ flex: "0 0 auto", maxHeight: "420px" }}>
      <table className="lcTable">
        <thead>
          <tr>
            {["IO Plan Type ID", "Plan Type", "Field", "Current value", "Expected IO value"].map((heading) => (
              <th key={heading}><span className="FP_Help_Text_Semibold">{heading}</span></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {changedRows.map((row) => (
            <tr key={`${row.planTypeId}-${row.field}`}>
              <td><span className="FP_Help_Text">{row.planTypeId}</span></td>
              <td><span className="FP_Help_Text">{row.planTypeName}</span></td>
              <td><span className="FP_Help_Text_Semibold">{IO_FIELD_LABELS[row.field]}</span></td>
              <td><span className="FP_Help_Text">{row.current || "(blank)"}</span></td>
              <td><span className="FP_Help_Text">{row.expected || "(blank)"}</span></td>
            </tr>
          ))}
          {result.missing.map((item) => (
            <tr key={`missing-${item.id}`}>
              <td><span className="FP_Help_Text">{item.id}</span></td>
              <td><span className="FP_Help_Text">{item.name}</span></td>
              <td><span className="FP_Help_Text_Semibold">Missing record</span></td>
              <td><span className="FP_Help_Text">(not uploaded)</span></td>
              <td><span className="FP_Help_Text">Present in IO</span></td>
            </tr>
          ))}
          {result.unexpected.map((item) => (
            <tr key={`unexpected-${item.planTypeId}`}>
              <td><span className="FP_Help_Text">{item.planTypeId}</span></td>
              <td><span className="FP_Help_Text">{item.planTypeName}</span></td>
              <td><span className="FP_Help_Text_Semibold">Unknown record</span></td>
              <td><span className="FP_Help_Text">Present in upload</span></td>
              <td><span className="FP_Help_Text">Not found in IO</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PlanTypeMappingPage;
